import { createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "logs-table" }
const _hoisted_2 = {
  key: 0,
  class: "table-container"
}
const _hoisted_3 = { class: "table-responsive" }
const _hoisted_4 = { class: "table table-striped table-hover" }
const _hoisted_5 = { class: "table-dark" }
const _hoisted_6 = { class: "alert alert-danger" }
const _hoisted_7 = { class: "table-container" }
const _hoisted_8 = { class: "filters-section" }
const _hoisted_9 = { class: "log-filters-compact" }
const _hoisted_10 = { class: "row g-1 align-items-end" }
const _hoisted_11 = ["title"]
const _hoisted_12 = ["value", "placeholder", "onInput"]
const _hoisted_13 = { class: "table-controls mb-3" }
const _hoisted_14 = { class: "row align-items-center" }
const _hoisted_15 = { class: "col-lg-3 col-md-6" }
const _hoisted_16 = { class: "d-flex align-items-center gap-2" }
const _hoisted_17 = ["value"]
const _hoisted_18 = { class: "col-lg-4 col-md-6" }
const _hoisted_19 = { class: "d-flex align-items-center gap-2" }
const _hoisted_20 = { class: "dropdown" }
const _hoisted_21 = ["data-bs-popper-config"]
const _hoisted_22 = { class: "dropdown-menu" }
const _hoisted_23 = { class: "form-check" }
const _hoisted_24 = ["id", "checked", "onChange"]
const _hoisted_25 = ["for"]
const _hoisted_26 = { class: "col-lg-4 col-md-6 text-md-end" }
const _hoisted_27 = { class: "text-muted" }
const _hoisted_28 = {
  ref: "scrollContainer",
  class: "table-responsive",
  style: {"overflow-x":"auto"}
}
const _hoisted_29 = { class: "table table-striped table-hover logs-table-expanded" }
const _hoisted_30 = { class: "table-dark" }
const _hoisted_31 = ["onClick"]
const _hoisted_32 = { class: "d-flex align-items-center justify-content-between" }
const _hoisted_33 = {
  key: 0,
  class: "sort-indicator"
}
const _hoisted_34 = {
  key: 0,
  class: "fas fa-sort-up"
}
const _hoisted_35 = {
  key: 1,
  class: "fas fa-sort-down"
}
const _hoisted_36 = {
  key: 2,
  class: "fas fa-sort text-muted"
}
const _hoisted_37 = ["title"]
const _hoisted_38 = {
  key: 0,
  class: "expanded-row"
}
const _hoisted_39 = ["colspan"]
const _hoisted_40 = {
  key: 0,
  "aria-label": "Pagination"
}
const _hoisted_41 = { class: "d-flex justify-content-between align-items-center" }
const _hoisted_42 = ["disabled"]
const _hoisted_43 = ["disabled"]
const _hoisted_44 = { class: "d-flex align-items-center gap-2" }
const _hoisted_45 = ["value", "max"]
const _hoisted_46 = { class: "text-muted" }

import { computed, useTemplateRef } from "vue";
import { FlexRender } from "@tanstack/vue-table";
import { useQuery } from "@tanstack/vue-query";
import { fetchStudents } from "../../../../api/queries/accountQueries.js";
import { queryKeys } from "../../../../api/queryKeys.js";
import { useStudentsTable } from "./useStudentsTable.js";
import StudentDetailsExpanded from "./StudentDetailsExpanded.vue";
import { useElementWidth } from "../composables/useElementWidth.js";
import { addDays } from "date-fns";

// Default date range for detail queries (today to today + 14 days)

export default {
  __name: 'StudentsTable',
  emits: ["column-filters-change"],
  setup(__props, { expose: __expose, emit: __emit }) {

const defaultFromDate = new Date();
const defaultUntilDate = addDays(new Date(), 14);

// Fetch students data
const { data: students, isPending, isError, error } = useQuery({
  queryKey: queryKeys.accountStudents(),
  queryFn: fetchStudents,
  staleTime: 5 * 60 * 1000, // 5 minutes
});

const { table, columnFilters } = useStudentsTable(() => students.value || []);

const scrollContainerWidth = useElementWidth(useTemplateRef("scrollContainer"));

// Pin expanded details to the visible width so they don't stretch with the wide table
const expandedContentStyle = computed(() =>
  scrollContainerWidth.value ? { width: `${scrollContainerWidth.value}px` } : undefined
);

// Full text on hover for values truncated by the cell's ellipsis
const cellTitle = (cell) => {
  const value = cell.getValue();
  return typeof value === "string" ? value : undefined;
};

const skeletonColumnCount = table.getAllColumns().length;

const textFilters = [
  { id: 'studentId', label: 'Matrikelnummer', placeholder: 'Matrikelnummer' },
  { id: 'lastname', label: 'Nachname', placeholder: 'Nachname' },
  { id: 'firstname', label: 'Vorname', placeholder: 'Vorname' },
  { id: 'program', label: 'Studiengang', placeholder: 'Studiengang' },
  { id: 'majorSubject', label: 'HF', placeholder: 'Hauptfach', title: 'Hauptfach' },
];

// Expose table instance to parent
__expose({ table });

// Emit column filter changes to parent
const emit = __emit;

// Client-side filter helpers for Task 5 (binds to LogFilters-like pattern)
const updateFilter = (id, value) => {
  if (!table) return;
  const currentFilters = table.getState().columnFilters;
  const newFilters = currentFilters.filter((f) => f.id !== id);
  if (value !== null && value !== '' && value !== undefined) {
    newFilters.push({ id, value });
  }
  table.setColumnFilters(newFilters);
};

const getFilterValue = (id) => {
  if (!table) return null;
  const filter = table.getState().columnFilters.find((f) => f.id === id);
  return filter ? filter.value : null;
};

// Helper function for skeleton cell widths
const getSkeletonWidth = (col) => {
  const widths = ["40px", "120px", "100px", "80px", "150px", "180px"];
  return widths[col - 1] || "100px";
};

// Watch for column filter changes and emit to parent
// Note: In a real implementation, we would watch columnFilters and emit changes
// For now, we'll just define the emit function

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createCommentVNode(" Loading State: keep skeletons only; filters moved to non-loading state "),
    (_unref(isPending))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createCommentVNode(" Table Controls Skeleton (no real inputs) "),
          _cache[5] || (_cache[5] = _createStaticVNode("<div class=\"table-controls mb-3\" data-v-4d2e361f><div class=\"row align-items-center\" data-v-4d2e361f><div class=\"col-md-6\" data-v-4d2e361f><div class=\"d-flex align-items-center gap-2\" data-v-4d2e361f><div class=\"skeleton skeleton-label\" data-v-4d2e361f></div><div class=\"skeleton skeleton-select\" data-v-4d2e361f></div></div></div><div class=\"col-md-6 text-md-end\" data-v-4d2e361f><div class=\"skeleton skeleton-info\" data-v-4d2e361f></div></div></div></div>", 1)),
          _createCommentVNode(" Table Skeleton "),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("table", _hoisted_4, [
              _createElementVNode("thead", _hoisted_5, [
                _createElementVNode("tr", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(skeletonColumnCount), (n) => {
                    return (_openBlock(), _createElementBlock("th", { key: n }, [...(_cache[4] || (_cache[4] = [
                      _createElementVNode("div", { class: "skeleton skeleton-header" }, null, -1 /* CACHED */)
                    ]))]))
                  }), 128 /* KEYED_FRAGMENT */))
                ])
              ]),
              _createElementVNode("tbody", null, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                  return _createElementVNode("tr", { key: n }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(skeletonColumnCount), (col) => {
                      return (_openBlock(), _createElementBlock("td", { key: col }, [
                        _createElementVNode("div", {
                          class: "skeleton skeleton-cell",
                          style: _normalizeStyle({ width: getSkeletonWidth(col) })
                        }, null, 4 /* STYLE */)
                      ]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ])
                }), 64 /* STABLE_FRAGMENT */))
              ])
            ])
          ])
        ]))
      : (_unref(isError))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createCommentVNode(" Error State "),
            _createElementVNode("div", _hoisted_6, [
              _cache[6] || (_cache[6] = _createElementVNode("i", { class: "fas fa-exclamation-triangle me-2" }, null, -1 /* CACHED */)),
              _createTextVNode(" " + _toDisplayString(_unref(error).message), 1 /* TEXT */)
            ])
          ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
        : (!_unref(students) || _unref(students).length === 0)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createCommentVNode(" Empty State "),
              _cache[7] || (_cache[7] = _createElementVNode("div", { class: "text-center py-5" }, [
                _createElementVNode("i", {
                  class: "fas fa-search text-muted",
                  style: {"font-size":"3rem"}
                }),
                _createElementVNode("h4", { class: "mt-3 text-muted" }, " Keine Studenten gefunden "),
                _createElementVNode("p", { class: "text-muted" }, " Passen Sie die Filter an oder versuchen Sie es später erneut. ")
              ], -1 /* CACHED */))
            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createCommentVNode(" Table "),
              _createElementVNode("div", _hoisted_7, [
                _createCommentVNode(" Client-side Filters (visible in non-loading state) "),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(textFilters, (filter) => {
                        return _createElementVNode("div", {
                          key: filter.id,
                          class: "col-md-4"
                        }, [
                          _createElementVNode("label", {
                            class: "form-label-sm",
                            title: filter.title || filter.label
                          }, _toDisplayString(filter.label), 9 /* TEXT, PROPS */, _hoisted_11),
                          _createElementVNode("input", {
                            value: getFilterValue(filter.id) || '',
                            type: "text",
                            class: "form-control form-control-sm",
                            placeholder: filter.placeholder,
                            onInput: (e) => updateFilter(filter.id, e.target.value)
                          }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_12)
                        ])
                      }), 64 /* STABLE_FRAGMENT */))
                    ])
                  ])
                ]),
                _createCommentVNode(" Table Controls "),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("div", _hoisted_14, [
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("div", _hoisted_16, [
                        _cache[9] || (_cache[9] = _createElementVNode("label", { class: "form-label mb-0" }, "Einträge pro Seite:", -1 /* CACHED */)),
                        _createElementVNode("select", {
                          value: _unref(table).getState().pagination.pageSize,
                          class: "form-select form-select-sm",
                          style: {"width":"auto"},
                          onChange: _cache[0] || (_cache[0] = (e) => _unref(table).setPageSize(Number(e.target.value)))
                        }, [...(_cache[8] || (_cache[8] = [
                          _createElementVNode("option", { value: "10" }, " 10 ", -1 /* CACHED */),
                          _createElementVNode("option", { value: "25" }, " 25 ", -1 /* CACHED */),
                          _createElementVNode("option", { value: "50" }, " 50 ", -1 /* CACHED */),
                          _createElementVNode("option", { value: "100" }, " 100 ", -1 /* CACHED */)
                        ]))], 40 /* PROPS, NEED_HYDRATION */, _hoisted_17)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_18, [
                      _createElementVNode("div", _hoisted_19, [
                        _cache[10] || (_cache[10] = _createElementVNode("label", { class: "form-label mb-0" }, "Spalten:", -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_20, [
                          _createElementVNode("button", {
                            class: "btn btn-outline-secondary btn-sm dropdown-toggle",
                            type: "button",
                            "data-bs-toggle": "dropdown",
                            "data-bs-popper-config": JSON.stringify({strategy: 'fixed'}),
                            "aria-expanded": "false"
                          }, " Anzeigen ", 8 /* PROPS */, _hoisted_21),
                          _createElementVNode("ul", _hoisted_22, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getAllLeafColumns().filter(col => col.getCanHide()), (column) => {
                              return (_openBlock(), _createElementBlock("li", {
                                key: column.id,
                                class: "dropdown-item-text"
                              }, [
                                _createElementVNode("div", _hoisted_23, [
                                  _createElementVNode("input", {
                                    id: `column-${column.id}`,
                                    class: "form-check-input",
                                    type: "checkbox",
                                    checked: column.getIsVisible(),
                                    onChange: $event => (column.toggleVisibility())
                                  }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_24),
                                  _createElementVNode("label", {
                                    for: `column-${column.id}`,
                                    class: "form-check-label"
                                  }, _toDisplayString(typeof column.columnDef.header === 'function'
                            ? column.columnDef.header()
                            : column.columnDef.header), 9 /* TEXT, PROPS */, _hoisted_25)
                                ])
                              ]))
                            }), 128 /* KEYED_FRAGMENT */))
                          ])
                        ])
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_26, [
                      _createElementVNode("small", _hoisted_27, " Zeige " + _toDisplayString(_unref(table).getState().pagination.pageIndex *
                  _unref(table).getState().pagination.pageSize +
                  1) + " bis " + _toDisplayString(Math.min(
                  (_unref(table).getState().pagination.pageIndex + 1) *
                    _unref(table).getState().pagination.pageSize,
                  _unref(table).getFilteredRowModel().rows.length
                )) + " von " + _toDisplayString(_unref(table).getFilteredRowModel().rows.length) + " Einträgen ", 1 /* TEXT */)
                    ])
                  ])
                ]),
                _createCommentVNode(" Responsive Table Wrapper with horizontal scroll "),
                _createElementVNode("div", _hoisted_28, [
                  _createElementVNode("table", _hoisted_29, [
                    _createElementVNode("thead", _hoisted_30, [
                      _createElementVNode("tr", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getFlatHeaders(), (header) => {
                          return (_openBlock(), _createElementBlock("th", {
                            key: header.id,
                            class: _normalizeClass({
                  sortable: header.column.getCanSort(),
                  'expand-column': header.id === 'expand',
                }),
                            onClick: $event => (header.column.getToggleSortingHandler()?.($event))
                          }, [
                            _createElementVNode("div", _hoisted_32, [
                              _createVNode(_unref(FlexRender), {
                                render: header.column.columnDef.header,
                                props: header.getContext()
                              }, null, 8 /* PROPS */, ["render", "props"]),
                              (header.column.getCanSort())
                                ? (_openBlock(), _createElementBlock("span", _hoisted_33, [
                                    (header.column.getIsSorted() === 'asc')
                                      ? (_openBlock(), _createElementBlock("i", _hoisted_34))
                                      : (header.column.getIsSorted() === 'desc')
                                        ? (_openBlock(), _createElementBlock("i", _hoisted_35))
                                        : (_openBlock(), _createElementBlock("i", _hoisted_36))
                                  ]))
                                : _createCommentVNode("v-if", true)
                            ])
                          ], 10 /* CLASS, PROPS */, _hoisted_31))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ]),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(table).getRowModel().rows, (row) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: row.id
                        }, [
                          _createElementVNode("tr", {
                            class: _normalizeClass(["main-row", { expanded: row.getIsExpanded() }])
                          }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.getVisibleCells(), (cell) => {
                              return (_openBlock(), _createElementBlock("td", {
                                key: cell.id,
                                class: _normalizeClass({ 'expand-column': cell.column.id === 'expand' }),
                                title: cellTitle(cell)
                              }, [
                                _createVNode(_unref(FlexRender), {
                                  render: cell.column.columnDef.cell,
                                  props: cell.getContext()
                                }, null, 8 /* PROPS */, ["render", "props"])
                              ], 10 /* CLASS, PROPS */, _hoisted_37))
                            }), 128 /* KEYED_FRAGMENT */))
                          ], 2 /* CLASS */),
                          _createCommentVNode(" Expanded row "),
                          (row.getIsExpanded())
                            ? (_openBlock(), _createElementBlock("tr", _hoisted_38, [
                                _createElementVNode("td", {
                                  colspan: row.getAllCells().length,
                                  class: "p-0"
                                }, [
                                  _createElementVNode("div", {
                                    class: "expanded-content",
                                    style: _normalizeStyle(expandedContentStyle.value)
                                  }, [
                                    _createVNode(StudentDetailsExpanded, {
                                      student: row.original,
                                      "default-from-date": _unref(defaultFromDate),
                                      "default-until-date": _unref(defaultUntilDate)
                                    }, null, 8 /* PROPS */, ["student", "default-from-date", "default-until-date"])
                                  ], 4 /* STYLE */)
                                ], 8 /* PROPS */, _hoisted_39)
                              ]))
                            : _createCommentVNode("v-if", true)
                        ], 64 /* STABLE_FRAGMENT */))
                      }), 128 /* KEYED_FRAGMENT */))
                    ])
                  ])
                ], 512 /* NEED_PATCH */),
                _createCommentVNode(" Pagination "),
                (_unref(table).getPageCount() > 1)
                  ? (_openBlock(), _createElementBlock("nav", _hoisted_40, [
                      _createElementVNode("div", _hoisted_41, [
                        _createElementVNode("div", null, [
                          _createElementVNode("button", {
                            class: "btn btn-outline-primary me-2",
                            disabled: !_unref(table).getCanPreviousPage(),
                            onClick: _cache[1] || (_cache[1] = $event => (_unref(table).previousPage()))
                          }, [...(_cache[11] || (_cache[11] = [
                            _createElementVNode("i", { class: "fas fa-chevron-left me-1" }, null, -1 /* CACHED */),
                            _createTextVNode(" Zurück ", -1 /* CACHED */)
                          ]))], 8 /* PROPS */, _hoisted_42),
                          _createElementVNode("button", {
                            class: "btn btn-outline-primary",
                            disabled: !_unref(table).getCanNextPage(),
                            onClick: _cache[2] || (_cache[2] = $event => (_unref(table).nextPage()))
                          }, [...(_cache[12] || (_cache[12] = [
                            _createTextVNode(" Weiter ", -1 /* CACHED */),
                            _createElementVNode("i", { class: "fas fa-chevron-right ms-1" }, null, -1 /* CACHED */)
                          ]))], 8 /* PROPS */, _hoisted_43)
                        ]),
                        _createElementVNode("div", _hoisted_44, [
                          _cache[13] || (_cache[13] = _createElementVNode("span", { class: "text-muted" }, "Seite", -1 /* CACHED */)),
                          _createElementVNode("input", {
                            value: _unref(table).getState().pagination.pageIndex + 1,
                            type: "number",
                            min: "1",
                            max: _unref(table).getPageCount(),
                            class: "form-control form-control-sm",
                            style: {"width":"80px"},
                            onChange: _cache[3] || (_cache[3] = (e) => _unref(table).setPageIndex(Number(e.target.value) - 1))
                          }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_45),
                          _createElementVNode("span", _hoisted_46, "von " + _toDisplayString(_unref(table).getPageCount()), 1 /* TEXT */)
                        ])
                      ])
                    ]))
                  : _createCommentVNode("v-if", true)
              ])
            ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
  ]))
}
}

}